import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Plus, Search, ArrowRight, HardHat, Building2, Hammer } from "lucide-react";

const empty = { name: "", client_name: "", address: "", original_estimate: 0, notes: "" };

export default function Projects() {
  const [projects, setProjects] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      toast.error(apiError(e));
      setProjects([]);
    }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!projects) return null;
    const term = q.trim().toLowerCase();
    if (!term) return projects;
    return projects.filter((p) =>
      [p.name, p.client_name, p.address].filter(Boolean).some((s) => s.toLowerCase().includes(term))
    );
  }, [projects, q]);

  async function createProject(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/projects", {
        ...form,
        original_estimate: Number(form.original_estimate) || 0,
      });
      toast.success("Project created");
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Portfolio</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Your projects</h1>
          <p className="text-sm text-slate-500 mt-1.5">All the builds you&apos;re tracking, at a glance.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search projects…"
              className="h-12 pl-9"
              data-testid="projects-search"
            />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="h-12 bg-brandblue hover:bg-brandblue-hover text-white px-5" data-testid="btn-new-project">
                <Plus className="h-4 w-4" /> New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">New project</DialogTitle>
              </DialogHeader>
              <form onSubmit={createProject} className="space-y-4" data-testid="new-project-form">
                <Field label="Project name" required>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12" data-testid="project-name" />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Client name">
                    <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="h-12" data-testid="project-client" />
                  </Field>
                  <Field label="Original estimate (AUD)">
                    <Input type="number" step="1" min="0" value={form.original_estimate} onChange={(e) => setForm({ ...form, original_estimate: e.target.value })} className="h-12" data-testid="project-estimate" />
                  </Field>
                </div>
                <Field label="Site address">
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-12" data-testid="project-address" />
                </Field>
                <Field label="Notes">
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} data-testid="project-notes" />
                </Field>
                <DialogFooter>
                  <Button type="submit" disabled={saving} className="h-12 bg-brandblue hover:bg-brandblue-hover text-white" data-testid="btn-save-project">
                    {saving ? "Saving…" : "Create project"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {filtered === null ? (
        <SkeletonGrid />
      ) : filtered.length === 0 ? (
        <EmptyState onCreate={() => setOpen(true)} hasSearch={!!q} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger" data-testid="projects-grid">
          {filtered.map((p) => <ProjectCard key={p.id} p={p} />)}
        </div>
      )}
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
        {label} {required && <span className="text-status-danger-text">*</span>}
      </Label>
      {children}
    </div>
  );
}

function ProjectCard({ p }) {
  const s = p.summary || {};
  const budget = s.project_original_estimate || 0;
  const used = Math.min(s.percent_budget_used || 0, 999);
  const over = s.over_under_budget < 0;
  const barColor = over ? "bg-status-danger-text" : used > 80 ? "bg-status-part-text" : "bg-status-paid-text";

  return (
    <Link to={`/projects/${p.id}`} data-testid={`project-card-${p.id}`}>
      <Card className="p-5 border border-border shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-transform duration-200 h-full flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Building2 className="h-3.5 w-3.5" />
              <span className="truncate">{p.client_name || "No client"}</span>
            </div>
            <h3 className="font-display font-bold text-xl text-slate-900 mt-1 truncate">{p.name}</h3>
            <div className="text-xs text-slate-500 mt-0.5 truncate">{p.address || "—"}</div>
          </div>
          <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-brandblue" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Stat label="Budget" value={fmtMoney(budget)} />
          <Stat label="Paid" value={fmtMoney(s.total_paid)} tone="paid" />
          <Stat label="Owing" value={fmtMoney(s.total_owing)} tone="part" />
          <Stat
            label={over ? "Over budget" : "Under budget"}
            value={fmtMoney(Math.abs(s.over_under_budget || 0))}
            tone={over ? "danger" : "paid"}
          />
        </div>

        <div>
          <div className="flex justify-between text-xs mb-1.5">
            <span className="text-slate-500 font-medium">Budget used</span>
            <span className="font-semibold text-slate-700" data-testid={`project-percent-${p.id}`}>{s.percent_budget_used || 0}%</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full ${barColor} transition-all duration-500`} style={{ width: `${Math.min(used, 100)}%` }} />
          </div>
        </div>

        <div className="text-[11px] text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
          <Hammer className="h-3 w-3" /> {s.trade_count || 0} trades · Created {fmtDate(p.created_at)}
        </div>
      </Card>
    </Link>
  );
}

function Stat({ label, value, tone = "neutral" }) {
  const toneCls = {
    neutral: "text-slate-900",
    paid: "text-status-paid-text",
    part: "text-status-part-text",
    danger: "text-status-danger-text",
  }[tone];
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-display font-semibold text-base ${toneCls}`}>{value}</div>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[0,1,2].map((i) => (
        <div key={i} className="h-64 bg-white border border-border rounded-lg animate-pulse" />
      ))}
    </div>
  );
}

function EmptyState({ onCreate, hasSearch }) {
  return (
    <Card className="p-12 text-center border-dashed border-2" data-testid="projects-empty">
      <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
        <HardHat className="h-7 w-7 text-slate-500" />
      </div>
      <h3 className="font-display text-xl font-semibold">{hasSearch ? "No matching projects" : "Start your first project"}</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
        {hasSearch ? "Try a different search term." : "Track quotes, trades and payments from day one."}
      </p>
      {!hasSearch && (
        <Button onClick={onCreate} className="mt-5 h-12 bg-brandblue hover:bg-brandblue-hover text-white px-6" data-testid="btn-empty-create">
          <Plus className="h-4 w-4" /> Create project
        </Button>
      )}
    </Card>
  );
}
